import { useEffect } from 'react'

/**
 * Mobile: no browser zoom (JS layer).
 *
 * `web/globals.css` sets `touch-action: pan-x pan-y` and `index.html` sets
 * `maximum-scale=1, user-scalable=no`, but iOS Safari ignores both for the
 * pinch gesture (since iOS 10). The only thing WebKit honours is
 * `preventDefault()` on the touch/gesture events themselves — non-passive
 * listeners on the document, installed once by `useNoZoom()` in the root
 * layout.
 *
 * The whiteboard opts out: Excalidraw handles pinch itself via pointer
 * events, so anything inside an element carrying `data-allow-zoom` is left
 * alone.
 */

/** Put this attribute on a container whose contents may pinch-zoom. */
export const ALLOW_ZOOM_ATTR = 'data-allow-zoom'
const ALLOW_ZOOM_SELECTOR = `[${ALLOW_ZOOM_ATTR}]`

/** Second tap within this window and distance of the first = double-tap. */
export const DOUBLE_TAP_MAX_DELAY_MS = 300
export const DOUBLE_TAP_MAX_DISTANCE_PX = 25

/**
 * Elements where a quick second tap has a meaning of its own (double-click
 * to select a word, rapid button presses). Never swallow those.
 */
const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'textarea',
  'select',
  'label',
  'summary',
  '[contenteditable]',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="textbox"]',
].join(',')

export interface PinchGesture {
  /** `touchstart` / `touchmove` carry a touch count; WebKit `gesture*` events are always a pinch. */
  kind: 'touch' | 'gesture'
  touchCount: number
  /** The event target sits inside a `data-allow-zoom` container. */
  zoomAllowed: boolean
}

/** Should this touch/gesture event be cancelled to stop the page from zooming? */
export function shouldBlockPinch(g: PinchGesture): boolean {
  if (g.zoomAllowed) return false
  if (g.kind === 'gesture') return true
  return g.touchCount > 1
}

export interface Tap {
  time: number
  x: number
  y: number
}

export interface DoubleTapCandidate {
  previous: Tap | null
  current: Tap
  zoomAllowed: boolean
  /** The tap landed on something interactive (button, link, editable text …). */
  interactive: boolean
}

/** Is this tap the second half of a double-tap that would zoom the page? */
export function shouldBlockDoubleTap(c: DoubleTapCandidate): boolean {
  if (c.zoomAllowed || c.interactive || !c.previous) return false
  const delay = c.current.time - c.previous.time
  if (delay < 0 || delay > DOUBLE_TAP_MAX_DELAY_MS) return false
  const dx = c.current.x - c.previous.x
  const dy = c.current.y - c.previous.y
  return Math.hypot(dx, dy) <= DOUBLE_TAP_MAX_DISTANCE_PX
}

function isZoomAllowedTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(ALLOW_ZOOM_SELECTOR) !== null
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null
}

/** Install the document-level zoom guard for the lifetime of the component. */
export function useNoZoom() {
  useEffect(() => {
    if (typeof document === 'undefined') return
    let lastTap: Tap | null = null

    const onTouch = (event: TouchEvent) => {
      if (
        shouldBlockPinch({
          kind: 'touch',
          touchCount: event.touches.length,
          zoomAllowed: isZoomAllowedTarget(event.target),
        })
      ) {
        event.preventDefault()
      }
    }

    const onGesture = (event: Event) => {
      if (
        shouldBlockPinch({
          kind: 'gesture',
          touchCount: 2,
          zoomAllowed: isZoomAllowedTarget(event.target),
        })
      ) {
        event.preventDefault()
      }
    }

    const onTouchEnd = (event: TouchEvent) => {
      // A multi-touch release is not a tap.
      if (event.touches.length > 0 || event.changedTouches.length !== 1) {
        lastTap = null
        return
      }
      const touch = event.changedTouches[0]
      const current: Tap = { time: event.timeStamp, x: touch.clientX, y: touch.clientY }
      const block = shouldBlockDoubleTap({
        previous: lastTap,
        current,
        zoomAllowed: isZoomAllowedTarget(event.target),
        interactive: isInteractiveTarget(event.target),
      })
      if (block) {
        event.preventDefault()
        lastTap = null
      } else {
        lastTap = current
      }
    }

    // Capture phase + non-passive: run before Excalidraw's own document
    // handlers and be allowed to call preventDefault().
    const opts: AddEventListenerOptions = { passive: false, capture: true }
    document.addEventListener('touchstart', onTouch, opts)
    document.addEventListener('touchmove', onTouch, opts)
    document.addEventListener('touchend', onTouchEnd, opts)
    document.addEventListener('gesturestart', onGesture, opts)
    document.addEventListener('gesturechange', onGesture, opts)
    return () => {
      document.removeEventListener('touchstart', onTouch, opts)
      document.removeEventListener('touchmove', onTouch, opts)
      document.removeEventListener('touchend', onTouchEnd, opts)
      document.removeEventListener('gesturestart', onGesture, opts)
      document.removeEventListener('gesturechange', onGesture, opts)
    }
  }, [])
}
